import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Collapse,
  Group,
  Select,
  Space,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RobotRequestFormConfigs, UpdateRobotParams, useBot, SvgCopy, copyToClipboard } from '@mygpt/app';

import { Asterisk } from '@/components/Common/Asterisk';
import { SvgTrash } from '@/components/Common/icon';
import DeleteModal from '@/components/Settings/deleteModal';
import { LANGUAGE_OPTIONS_LIST } from '@/constant';
import { useCopy } from '@/hooks/useCopy';
import { useGPTStore } from '@/store';
import { useDatasetStore } from '@/store/dataset';
import { Tags } from './Tags';

import classes from '../index.module.css';

type LanguageKeyType = keyof typeof LANGUAGE_OPTIONS_LIST;

export interface BotModelOption {
  value: string;
  label: string;
  enabled: boolean;
}

export const buildBotModelOptions = (
  models: BotModelOption[],
  currentModel: string | undefined,
  unavailableLabel: string
) => {
  const enabledOptions = models
    .filter((model) => model.enabled)
    .map((model) => ({ value: model.value, label: model.label }));

  if (!currentModel || enabledOptions.some((model) => model.value === currentModel)) {
    return enabledOptions;
  }

  const current = models.find((model) => model.value === currentModel);
  return [
    {
      value: currentModel,
      label: `${current?.label || currentModel} (${unavailableLabel})`,
      disabled: true,
    },
    ...enabledOptions,
  ];
};

interface BotModelSelectProps {
  models: BotModelOption[];
  value: string | undefined;
  onConfirmedChange: (value: string) => void;
  isLocalMode?: boolean;
}

export const BotModelSelect = ({ models, value, onConfirmedChange, isLocalMode = false }: BotModelSelectProps) => {
  const { t } = useTranslation();
  const options = useMemo(
    () => buildBotModelOptions(models, value, t('settings.openai.unavailable')),
    [models, t, value]
  );
  const handleChange = useCallback(
    (newModel: string | null) => {
      if (!newModel || newModel === value) return;
      modals.openConfirmModal({
        title: t('settings.openai.confirm_title'),
        centered: true,
        children: <Text size="sm">{t('settings.openai.confirm_desc')}</Text>,
        labels: { confirm: t('confirm'), cancel: t('cancel') },
        onConfirm: () => onConfirmedChange(newModel),
      });
    },
    [onConfirmedChange, t, value]
  );

  if (isLocalMode) return null;
  return (
    <Select
      label={
        <Text fw={400} mb={10}>
          {t('settings.openai.label')}
        </Text>
      }
      data={options}
      value={value}
      onChange={handleChange}
      placeholder={t('settings.openai.label')}
      allowDeselect={false}
    />
  );
};

interface Props {
  documentForm: UseFormReturnType<RobotRequestFormConfigs>;
  onOverrideChangeForConfigForm: (prop: string) => (data?: any) => void;
  robotBaseInfoForm: UseFormReturnType<UpdateRobotParams>;
  onOverrideChangeForRobotBaseInfoForm: (prop: keyof UpdateRobotParams) => (data?: any) => void;
  showDangerZone?: boolean;
}

export const General = ({
  documentForm,
  onOverrideChangeForConfigForm,
  onOverrideChangeForRobotBaseInfoForm,
  robotBaseInfoForm,
  showDangerZone = false,
}: Props) => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { userInfo, configInfo } = useGPTStore();
  const { isCloning } = useDatasetStore();

  const [copied, setCopied] = useCopy(false, 1000);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteOpened, { close: closeDelete, open: openDelete }] = useDisclosure(false);
  const [dangerOpened, { toggle: toggleDanger }] = useDisclosure(false);

  const languageList = () => {
    const arr = [];
    for (const key in LANGUAGE_OPTIONS_LIST as { [key: string]: string }) {
      arr.push({
        label: LANGUAGE_OPTIONS_LIST[key as LanguageKeyType],
        value: key,
      });
    }
    return arr;
  };

  const RobotTypeData = useMemo(() => {
    return [
      {
        label: t('create.form.label.robot_type_label.rag'),
        value: 'rag',
      },
      { label: t('create.form.label.robot_type_label.digital_human'), value: 'digital_human' },
    ];
  }, [t]);

  const digitalHumanAccess = useMemo(() => {
    const inviteValue = userInfo?.userconfigs.find((config) => config.key === 'enable_digital_human_interface');
    return inviteValue && inviteValue.value === 'True';
  }, [userInfo]);

  return (
    <>
      <div
        className="relative rounded-xl bg-white flex-1 p-6"
        style={{
          boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50',
        }}
      >
        <TextInput
          readOnly
          defaultValue={botId}
          variant="filled"
          label={
            <Text fw={400} mb={10}>
              ID
            </Text>
          }
          rightSection={
            <Tooltip label={'Copy'} withArrow>
              <ActionIcon
                variant="subtle"
                color="violet.5"
                onClick={() => {
                  const copied = copyToClipboard(botId);
                  setCopied(!!copied);
                }}
              >
                {copied ? <IconCheck color="teal" size="1rem" /> : <SvgCopy />}
              </ActionIcon>
            </Tooltip>
          }
          className={classes.textinput}
        />
        <Space h="lg" />
        <Stack>
          <TextInput
            label={
              <Text fw={400} mb={10}>
                {t('create.form.label.name')}
                <Asterisk />
              </Text>
            }
            maxLength={100}
            placeholder={t('create.form.placeholder.name')}
            {...robotBaseInfoForm.getInputProps('name')}
            onBlur={onOverrideChangeForRobotBaseInfoForm('name')}
          />
          <TextInput
            label={
              <Text fw={400} mb={10}>
                {t('create.form.label.subject_name')}
              </Text>
            }
            maxLength={50}
            description={<span className="text-gray-500">{t('create.form.label.subject_name_desc')}</span>}
            placeholder={t('create.form.placeholder.subject_name')}
            {...robotBaseInfoForm.getInputProps('subject_name')}
            onBlur={onOverrideChangeForRobotBaseInfoForm('subject_name')}
          />
          <Textarea
            label={
              <Text fw={400} mb={10}>
                {t('create.form.label.desc')}
                <Asterisk />
              </Text>
            }
            placeholder={t('create.form.placeholder.desc')}
            minRows={4}
            {...robotBaseInfoForm.getInputProps('discrible')}
            onBlur={onOverrideChangeForRobotBaseInfoForm('discrible')}
          />
          <Select
            label={
              <Text fw={400} mb={10}>
                {t('create.form.language')}
              </Text>
            }
            data={languageList()}
            {...documentForm.getInputProps('source_lang')}
            placeholder={t('create.form.language_placeholder')}
            onChange={onOverrideChangeForConfigForm('source_lang')}
            allowDeselect={false}
          />
          <BotModelSelect
            models={configInfo?.default_models || []}
            value={documentForm.values.openai_model}
            onConfirmedChange={(model) => onOverrideChangeForConfigForm('openai_model')(model)}
            isLocalMode={import.meta.env.VITE_MODE === 'local'}
          />
          {digitalHumanAccess && (
            <Select
              label={
                <Text fw={400} mb={10}>
                  {t('create.form.label.robot_type')}
                </Text>
              }
              data={RobotTypeData}
              {...robotBaseInfoForm.getInputProps('robot_type')}
              onChange={onOverrideChangeForRobotBaseInfoForm('robot_type')}
              allowDeselect={false}
            />
          )}
          {/* <Select
          label={
            <Text fw={400} mb={10}>
              {t('create.form.label.visibility')}
            </Text>
          }
          data={[
            { value: 'private', label: t('private_text') },
            { value: 'public', label: t('public_text') },
          ]}
          {...robotBaseInfoForm.getInputProps('ai_type')}
          onChange={() => {
            // 如果需要重新使用该设置项，那么实现用户选择后就保存的办法是使用 useSubmitFormAsync
            //
            // 原因是没有其他办法拿到最新的表单数据。
            // 可以测试下，比如在这里打印：console.log(robotBaseInfoForm.values.ai_type);
          }}
        /> */}

          {/* Tag Management Section - 仅 local 模式可用 */}
          {import.meta.env.VITE_MODE === 'local' && <Tags />}
        </Stack>
      </div>
      {showDangerZone && (
        <>
          <Space h="xl" />
          <Box
            className="rounded-lg"
            style={{
              border: '1px solid var(--mantine-color-red-2)',
              backgroundColor: 'var(--mantine-color-red-0)',
            }}
          >
            <Group justify="space-between" p="lg" style={{ cursor: 'pointer' }} onClick={toggleDanger}>
              <Text fw={600} size="md" c="red.7">
                {t('settings.delete.danger_zone_title')}
              </Text>
              <IconChevronDown
                size={18}
                color="var(--mantine-color-red-7)"
                style={{
                  transform: dangerOpened ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Group>
            <Collapse in={dangerOpened}>
              <Box px="lg" pb="lg">
                <Text size="sm" c="gray.7" mb={12}>
                  {t('settings.delete.desc')}
                </Text>
                <Checkbox
                  label={t('settings.delete.confirm')}
                  checked={deleteConfirm}
                  onChange={() => setDeleteConfirm(!deleteConfirm)}
                  mb={12}
                />
                <Button
                  variant="outline"
                  color="red"
                  onClick={openDelete}
                  disabled={!deleteConfirm}
                  leftSection={<SvgTrash />}
                >
                  {t('delete_btn')}
                </Button>
              </Box>
            </Collapse>
          </Box>
          <DeleteModal
            id={botId}
            name={robotBaseInfoForm.values.name || ''}
            opened={deleteOpened}
            onClose={closeDelete}
            title={t('embeddings.chatbot.delete.title')}
            centered
            isCloning={isCloning}
          />
        </>
      )}
    </>
  );
};
